import React, { useState, useEffect, useRef } from 'react';
import {  getUserId } from '../../../utils/api';
import StatusDropdown from '../../common/StatusDropdown';
import PriorityDropdown from '../../common/PriorityDropdown';

// Helper function to format date for date input (yyyy-MM-dd)
export const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            return dateValue;
        }
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (e) {
        return '';
    }
};

// Helper function to map assignee data
export const mapAssignee = (assigneeData) => {
    if (!assigneeData) return null;
    return {
        ID: assigneeData.user_id || assigneeData.ID || assigneeData.id,
        id: assigneeData.user_id || assigneeData.ID || assigneeData.id,
        display_name: assigneeData.display_name || assigneeData.name,
        name: assigneeData.display_name || assigneeData.name,
        image: assigneeData.assignee || assigneeData.image || assigneeData.avatar_url,
        avatar_url: assigneeData.assignee || assigneeData.image || assigneeData.avatar_url,
        user_email: assigneeData.user_email || assigneeData.email,
        created_by: assigneeData.created_by || assigneeData.createdBy,
        updated_by: assigneeData.updated_by || assigneeData.updatedBy
    };
};

// Helper function to map multiple assignees
export const mapAssignees = (assigneesData) => {
    if (!assigneesData || !Array.isArray(assigneesData)) return [];
    return assigneesData.map(mapAssignee).filter(Boolean);
};

const TaskRow = ({ 
    task, 
    parentTaskData = null,
    taskListId, 
    project, 
    onUpdate, 
    onDelete, 
    addAssigneeToTask, 
    searchUsers, 
    onDragStart, 
    onDragOver, 
    onDrop, 
    onDragEnd, 
    isDragging, 
    isDragOver, 
    onOpenDetail, 
    isSubtask = false, 
    parentTaskId = null, 
    handleAddSubtask, 
    showSubtasks, 
    setShowSubtasks, 
    subtasksCount = 0,
    // For MyTasksView - when project is not available
    projectId = null,
    useGlobalUserSearch = false
}) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editTitle, setEditTitle] = useState(task.title || task.name || '');
    
    const [assignees, setAssignees] = useState(() => {
        const assigneesData = task.assignees || task.users || [];
        return mapAssignees(assigneesData);
    });
    const [dueDate, setDueDate] = useState(task.due_date || task.dueDate || task.due_at || '');
    const [priority, setPriority] = useState(task.priority !== undefined ? task.priority : 0);
    const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
    const [showDueDatePicker, setShowDueDatePicker] = useState(false);
    const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
    const [assigneeSearchResults, setAssigneeSearchResults] = useState([]);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const assigneeSearchRef = useRef(null);
    const assigneeDropdownRef = useRef(null);
    const actionMenuRef = useRef(null);
    const titleInputRef = useRef(null);
    const datePickerRef = useRef(null);
    const datePickerWrapperRef = useRef(null);
    const previousDateValueRef = useRef(dueDate);

    const currentUserId = getUserId();

    const isTaskOwner = task.created_by === parseInt(currentUserId);

    const isParentTaskOwner = parentTaskData && parentTaskData.created_by === parseInt(currentUserId);


    // Sync state with task prop changes
    useEffect(() => {
        setEditTitle(task.title || task.name || '');
        const assigneesData = task.assignees || task.users || [];
        setAssignees(mapAssignees(assigneesData));
        const newDueDate = task.due_date || task.dueDate || task.due_at || '';
        const formattedDate = formatDateForInput(newDueDate);
        setDueDate(formattedDate);
        previousDateValueRef.current = formattedDate;
        setPriority(task.priority !== undefined ? task.priority : 0);


    }, [task]);

    // Filter assignee search - either from project users or global search
    useEffect(() => {
        if (!assigneeSearchQuery.trim()) {
            setAssigneeSearchResults([]);
            setShowAssigneeDropdown(false);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            if (useGlobalUserSearch) {
                // For MyTasksView - use global user search
                try {
                    const response = await searchUsers({ search: assigneeSearchQuery, size: 20 });
                    if (response && response.data && response.data.users) {
                        const users = response.data.users.users || [];
                        const filteredUsers = users.filter(user => {
                            const userId = user.ID || user.id;
                            const isAlreadyAssigned = assignees.some(a => (a.ID || a.id) === userId);
                            return !isAlreadyAssigned;
                        });
                        setAssigneeSearchResults(filteredUsers);
                        setShowAssigneeDropdown(true);
                    }
                } catch (err) {
                    console.error('Search users error:', err);
                }
            } else {
                // For SingleProjectView - use project users
                const projectUsers = project?.users || [];
                const searchLower = assigneeSearchQuery.toLowerCase().trim();
                
                const filteredUsers = projectUsers.filter(user => {
                    const userId = user.ID || user.id;
                    const isAlreadyAssigned = assignees.some(a => (a.ID || a.id) === userId);
                    if (isAlreadyAssigned) return false;
                    
                    const name = (user.display_name || user.name || '').toLowerCase();
                    const email = (user.user_email || user.email || '').toLowerCase();
                    return name.includes(searchLower) || email.includes(searchLower);
                });
                
                setAssigneeSearchResults(filteredUsers);
                setShowAssigneeDropdown(true);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [assigneeSearchQuery, project?.users, assignees, useGlobalUserSearch, searchUsers]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target) &&
                assigneeSearchRef.current && !assigneeSearchRef.current.contains(event.target)) {
                setShowAssigneeDropdown(false);
            }
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowActionMenu(false);
            }
            if (showDueDatePicker && 
                datePickerWrapperRef.current && 
                !datePickerWrapperRef.current.contains(event.target) &&
                !event.target.closest('.asana-due-date-wrapper')) {
                setShowDueDatePicker(false);
            }
        };

        if (showAssigneeDropdown || showActionMenu || showDueDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAssigneeDropdown, showActionMenu, showDueDatePicker]);

    const handleSelectAssignee = async (user) => {
        setAssigneeSearchQuery('');
        setShowAssigneeDropdown(false);
        const assigneeId = user.ID || user.id;
        addAssigneeToTask(projectId, task.id, taskListId, assigneeId);
    };
    
    const handleRemoveAssignee = async (assigneeId, e) => {
        e.stopPropagation();
        const currentProjectId = projectId || project?.id || task.project_id;

        
        if(onUpdate) {
            onUpdate(currentProjectId, task.id, taskListId, { assignee_id: assigneeId, action: 'remove_assignee' });
        }
    };

    const handleSaveTitle = async () => {
        const currentProjectId = projectId || project?.id || task.project_id;
        const currentTaskListId = taskListId || task.task_list_id;

        
        if (editTitle.trim() && editTitle.trim() !== (task.title || task.name)) {
            if (onUpdate) {
                onUpdate(currentProjectId, task.id, currentTaskListId, { title: editTitle.trim(), action: 'update_title' });
            }
        }
        setIsEditingTitle(false);
    };

    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveTitle();
        } else if (e.key === 'Escape') {
            setEditTitle(task.title || task.name || '');
            setIsEditingTitle(false);
        }
    };

    const handleDueDateChange = async (newDate) => {
        setDueDate(newDate);
        const currentProjectId = projectId || project?.id || task.project_id;
        const currentTaskListId = taskListId || task.task_list_id;
        
        if (onUpdate) {
            onUpdate(currentProjectId, task.id, currentTaskListId, { due_date: newDate || '', action: 'update_due_date' });
        }
    };
    
    const handleDateInputChange = (e) => {
        const newDate = e.target.value;
        const previousDate = previousDateValueRef.current;

        if (newDate && newDate !== previousDate && newDate !== dueDate) {
            previousDateValueRef.current = newDate;
            handleDueDateChange(newDate);
        }
    };

    const handleDelete = () => {
        const confirmMessage = isSubtask ? 'Are you sure you want to delete this subtask?' : 'Are you sure you want to delete this task?';
        if (window.confirm(confirmMessage)) {
            if (onDelete) {
                const currentProjectId = projectId || project?.id || task.project_id;
                const currentTaskListId = taskListId || task.task_list_id;
                onDelete(currentProjectId, task.id, currentTaskListId, task?.parent_id || null);
            }
        }
        setShowActionMenu(false);
    };

    const handleStatusChange = async (newStatus) => {
        const currentProjectId = projectId || project?.id || task.project_id;
        const currentTaskListId = taskListId || task.task_list_id;
        const parentTaskId = task?.parent_id || null;
        
        if (onUpdate) {
            onUpdate(currentProjectId, task.id, currentTaskListId, { status: newStatus, action: 'update_status', parent_task_id: parentTaskId });
        }
    };

    const handlePriorityChange = async (newPriority) => {
        const currentProjectId = projectId || project?.id || task.project_id;
        const currentTaskListId = taskListId || task.task_list_id;
        const priorityValue = parseInt(newPriority, 10);
        setPriority(priorityValue);
        
        if (onUpdate) {
            onUpdate(currentProjectId, task.id, currentTaskListId, { priority: priorityValue, action: 'update_priority' });
        }
    };

    const handleOpenDetail = () => {
        if (onOpenDetail) {
            const currentProjectId = projectId || project?.id || task.project_id;
            if (currentProjectId) {
                onOpenDetail(currentProjectId,task.id);
            } else {
                onOpenDetail(task.id);
            }
        }
    };

    // Determine which users to show in dropdown when no search query
    const getAvailableUsers = () => {
        if (useGlobalUserSearch) {
            return assigneeSearchResults;
        }
        return (project?.users || []).filter(user => {
            const userId = user.ID || user.id;
            return !assignees.some(a => (a.ID || a.id) === userId);
        });
    };


    return (
        <tr 
            className={`${isSubtask ? 'asana-subtask-row' : 'asana-task-row'} ${isDragging ? 'asana-task-dragging' : ''} ${isDragOver ? 'asana-task-drag-over' : ''} ${onDragStart ? 'draggableRow' : ''}`}
            // Ensure draggable is a strict boolean so the first drag always works
            draggable={!isSubtask && !!onDragStart}
            onDragStart={!isSubtask && onDragStart ? (e) => onDragStart(e, task.id, taskListId) : undefined}
            onDragOver={!isSubtask && onDragOver ? (e) => onDragOver(e, task.id, taskListId) : undefined}
            onDrop={!isSubtask && onDrop ? (e) => onDrop(e, task.id) : undefined}
            onDragEnd={!isSubtask && onDragEnd ? (e) => onDragEnd(e) : undefined}
        >
            <td className="asana-col-name" style={{  paddingLeft: isSubtask ? '60px' : undefined }} colSpan={isSubtask ? 4 : 1}>
                <div className="asana-task-name-cell">
                    {!isSubtask && onDragStart && (
                        <div className="asana-drag-handle">
                            <i className="fa-solid fa-grip-vertical"></i>
                        </div>
                    )}
                    {/* Status Dropdown */}
                    <StatusDropdown
                        currentStatus={task.status ?? 0}
                        onStatusChange={handleStatusChange}
                        className="asana-status-wrapper"
                        size="medium"
                    />
                    {isEditingTitle ? (
                        <input
                            ref={titleInputRef}
                            type="text"
                            className="asana-task-title-input"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onBlur={handleSaveTitle}
                            onKeyDown={handleTitleKeyDown}
                            autoFocus
                        />
                    ) : (
                        <div className="asana-task-title-wrapper" style={{ opacity: task.status === 1 ? 0.5 : 1 }}>
                            <div className="asana-task-title-left">
                                <span 
                                    className={`asana-task-title ${task.status == 1 ? 'asana-completed-task' : ''}`}
                                    onClick={() => {
                                        setIsEditingTitle(true);
                                        setEditTitle(task.title || task.name || '');
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {task.title || task.name}
                                </span>
                                {/* Show subtask indicator icon if task has subtasks */}
                                {subtasksCount > 0 && (
                                    <span 
                                        className="asana-subtask-indicator"
                                        title={`${subtasksCount} subtask${subtasksCount > 1 ? 's' : ''}`}
                                    >
                                        <i className="fa-solid fa-list-check"></i>
                                    </span>
                                )}
                                {isSubtask && task.subtasks && task.subtasks.length > 0 && (
                                    <span 
                                        className="asana-subtask-indicator"
                                        title={`${task.title}`}
                                    >
                                        <i className="fa-solid fa-list-check"></i>
                                    </span>
                                )}
                                {/* Show project badge for MyTasksView */}
                                {useGlobalUserSearch && task.project && (
                                    <span className="asana-task-project-badge" title={task.project.title || task.project.name}>
                                        <i className="fa-solid fa-folder"></i>
                                        {task.project.title || task.project.name}
                                    </span>
                                )}
                            </div>
                            {!isSubtask && handleAddSubtask && (
                                <>
                                    <button
                                        type="button"
                                        className="asana-add-subtask-btn"
                                        onClick={handleAddSubtask}
                                        title="Add subtask"
                                    >
                                        <i className="fa-solid fa-plus"></i>
                                    </button>
                                    {subtasksCount > 0 && setShowSubtasks && (
                                        <span
                                            type="button"
                                            className="asana-subtask-indicator-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowSubtasks(!showSubtasks);
                                            }}
                                            title={showSubtasks ? 'Hide subtasks' : 'Show subtasks'}
                                        >
                                            {showSubtasks ? 'Hide subtask' : 'Show subtask'}
                                        </span>
                                    )}
                                </>
                            )}
                            <button
                                type="button"
                                className="asana-task-action-btn"
                                onClick={handleOpenDetail}
                            >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            </td>
            <td className="asana-col-assignee" style={{ opacity: task.status === 1 ? 0.5 : 1, display: isSubtask ? 'none' : 'table-cell' }}>
                <div className="asana-assignee-select-wrapper" ref={assigneeSearchRef}>
                    {assignees.length > 0 ? (
                        <div 
                            className="asana-assignees-container"
                            onClick={() => {
                                setShowAssigneeDropdown(true);
                                setAssigneeSearchQuery('');
                            }}
                        >
                            {assignees.map((assignee) => (
                                <div 
                                    key={assignee.ID || assignee.id}
                                    className="asana-assignee-avatar-wrapper"
                                    title={assignee.display_name || assignee.name}
                                >
                                    <div className="asana-assignee-avatar">
                                        {assignee.image || assignee.avatar_url || assignee.assignee ? (
                                            <img src={assignee.image || assignee.avatar_url || assignee.assignee} alt={assignee.display_name || assignee.name} />
                                        ) : (
                                            <div className="asana-assignee-placeholder">
                                                {(assignee.display_name || assignee.name || 'U').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    {(isTaskOwner || assignee.created_by === parseInt(currentUserId)) && (
                                        <button
                                            type="button"
                                            className="asana-remove-assignee"
                                            onClick={(e) => handleRemoveAssignee(assignee.ID || assignee.id, e)}
                                        >
                                            <i className="fa-solid fa-times"></i>
                                        </button>
                                    )}
                                </div>
                            ))}
                            <div 
                                className="asana-add-assignee-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAssigneeDropdown(true);
                                    setAssigneeSearchQuery('');
                                }}
                            >
                                <i className="fa-solid fa-plus"></i>
                            </div>
                        </div>
                    ) : (
                        <div 
                            className="asana-assignee-placeholder-clickable"
                            onClick={() => setShowAssigneeDropdown(true)}
                        >
                            <i className="fa-solid fa-user-plus"></i>
                        </div>
                    )}
                    {showAssigneeDropdown && (
                        <div className="asana-assignee-dropdown" ref={assigneeDropdownRef}>
                            <input
                                type="text"
                                className="asana-assignee-search-input"
                                placeholder="Search assignee..."
                                value={assigneeSearchQuery}
                                onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                                autoFocus
                            />
                            {assigneeSearchQuery.trim() ? (
                                assigneeSearchResults.length > 0 ? (
                                    <div className="asana-assignee-results">
                                        {assigneeSearchResults.map((user) => (
                                            <div
                                                key={user.ID || user.id}
                                                className="asana-assignee-result-item"
                                                onClick={() => handleSelectAssignee(user)}
                                            >
                                                <div className="asana-assignee-avatar">
                                                    {user.image || user.avatar_url ? (
                                                        <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                                    ) : (
                                                        <div className="asana-assignee-placeholder">
                                                            {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                                <span>{user.display_name || user.name || 'User'}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="asana-search-empty">No users found</div>
                                )
                            ) : (
                                <div className="asana-assignee-results">
                                    {getAvailableUsers().map((user) => (
                                        <div
                                            key={user.ID || user.id}
                                            className="asana-assignee-result-item"
                                            onClick={() => handleSelectAssignee(user)}
                                        >
                                            <div className="asana-assignee-avatar">
                                                {user.image || user.avatar_url ? (
                                                    <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                                ) : (
                                                    <div className="asana-assignee-placeholder">
                                                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <span>{user.display_name || user.name || 'User'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </td>
            <td className="asana-col-due" style={{ opacity: task.status === 1 ? 0.5 : 1, display: isSubtask ? 'none' : 'table-cell' }}>
                <div className="asana-due-date-wrapper">
                    {dueDate ? (
                        <div 
                            className="asana-due-date-selected"
                            onClick={() => setShowDueDatePicker(true)}
                        >
                            <span>{new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <button
                                type="button"
                                className="asana-remove-due-date"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDueDateChange('');
                                }}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                    ) : (
                        <div 
                            className="asana-due-date-placeholder-clickable"
                            onClick={() => setShowDueDatePicker(true)}
                        >
                            <i className="fa-solid fa-calendar"></i>
                        </div>
                    )}
                    {showDueDatePicker && (
                        <div className="asana-due-date-picker-wrapper" ref={datePickerWrapperRef}>
                            <input
                                ref={datePickerRef}
                                type="date"
                                className="asana-due-date-picker"
                                value={formatDateForInput(dueDate)}
                                onChange={handleDateInputChange}
                                autoFocus
                            />
                        </div>
                    )}
                </div>
            </td>
            <td className="asana-col-priority" style={{ opacity: task.status === 1 ? 0.5 : 1, display: isSubtask ? 'none' : 'table-cell' }}>
                <PriorityDropdown
                    currentPriority={priority}
                    onPriorityChange={handlePriorityChange}
                    className="asana-priority-wrapper"
                    showAsPill={true}
                />
            </td>
            <td className="asana-col-actions">
                <div className="asana-task-actions-wrapper" ref={actionMenuRef}>
                    <button
                        type="button"
                        className="asana-task-action-btn"
                        onClick={() => setShowActionMenu(!showActionMenu)}
                    >
                        <i className="fa-solid fa-ellipsis-v"></i>
                    </button>
                    {showActionMenu && (
                        <div className="asana-task-action-menu">
                            <button
                                type="button"
                                className="asana-task-action-menu-item"
                                onClick={() => {
                                    handleOpenDetail();
                                    setShowActionMenu(false);
                                }}
                            >
                                <i className="fa-solid fa-eye"></i>
                                View
                            </button>
                            {(isTaskOwner || isParentTaskOwner) && (
                                <button
                                    type="button"
                                    className="asana-task-action-menu-item asana-task-action-delete"
                                    onClick={handleDelete}
                                >
                                    <i className="fa-solid fa-trash"></i>
                                    Delete
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
};

export default TaskRow;

